import { AxiosError, isAxiosError } from 'axios';
import { getMutationErrorMessage } from 'src/utils/error';

/**
 * CRUD-scoped error copy. getMutationErrorMessage only special-cases email/username/slug
 * and otherwise returns a generic localized string, so a delete refusal reads "There was
 * a problem!" instead of the reason the server actually gave. Preferring the server's
 * message app-wide would swap localized copy for raw server English on every page, so the
 * preference lives here, used only by CRUD pages.
 */
export function resolveCrudError(error: unknown): string | null {
  if (!error) {
    return null;
  }
  const message = (error as AxiosError)?.response?.data as { message?: unknown } | undefined;
  if (typeof message?.message === 'string' && message.message.trim()) {
    return message.message;
  }
  // A guard the page raised itself (issues.tsx's toPayload) has no server payload to read, so
  // its own message is the only useful copy. Discriminated by TYPE, not by payload shape:
  // AxiosError assigns `response` only inside `if (response)` (axios/lib/core/AxiosError.js:30),
  // so a network failure has no `response` property either — testing `'response' in error` would
  // hand "Network Error" (raw English) to a Vietnamese UI. Axios errors keep the localized
  // mapping below whether or not they carry a response.
  if (error instanceof Error && !isAxiosError(error)) {
    return error.message;
  }
  return getMutationErrorMessage(error as AxiosError);
}
